import { DataPicker, Radio, Typography } from '@/ui-kit/';
import styles from './date-and-gender-registartion-form.module.scss';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { registrationStepDateSchema, RegistrationStepDateType } from '@/schemes/registration-step-date-schema';
import { zodResolver } from '@hookform/resolvers/zod';
import { debounce, throttle } from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { type Profile } from '@/types/profile';
import { ButtonSubmitStep } from '@/components/form-registration-v4/components/button-submit-step';
import { eventsApi } from '@/api';
import { callYaMetrika } from '@/utils/call-ya-metrika';
import { useToast } from '@/store/useToast';
import { SuccessNotificationIcon } from '@/assets/icons';
import clsx from 'clsx';
import { FieldBlock } from '@/components/field-block';
import { registrationStepDateAndGenderSchema, RegistrationStepDateAndGenderType } from '@/schemes/registrationV4/registration-step-date-and-gender-schema';
import { RadioCupertino } from '@/ui-kit/radio-cupertino';
import { Trans, useTranslation } from 'react-i18next';
import { SignUpActions } from '@/components/sign-up-actions/sign-up-actions';

interface Props {
    userData?: Profile | null;
    onUpdateUserData: (data: RegistrationStepDateType) => Promise<any>;
    nextStep: () => void;
}

export const DateAndGenderRegistartionForm: React.FC<Props> = ({ userData, onUpdateUserData, nextStep }) => {
    const showToast = useToast((state) => state.show);
    useEffect(() => {
        callYaMetrika('new_dob_screen4');
        eventsApi.updateUserEvent('new_dob_screen4');
    }, []);
    
    const initialValues = useMemo(() => {
        return {
            ...(
                userData?.dob ?
                    { dob: userData.dob}
                :
                    {}
            ),
            gender: userData?.gender ?? undefined,
        };
    }, [userData]);

    const [isLoading, setIsLoading] = useState(false);

    const methods = useForm<RegistrationStepDateAndGenderType>({
        resolver: zodResolver(registrationStepDateAndGenderSchema),
        defaultValues: initialValues,
    });
    const {
        handleSubmit,
        formState: { errors, isDirty, isValid },
        watch,
        register,
    } = methods;

    const onSubmit = async (data: RegistrationStepDateAndGenderType) => {
        try {
            setIsLoading(true);
            /*
             * for update only dirty data use code below
             * if (isDirty) {
             *    await onUpdateUserData(data);
             * }
             */
            await onUpdateUserData(data);
            /*
             * await get user data
             * force not call nextStep after update user data
             * nextStep();
             */
            nextStep();
            eventsApi.updateUserEvent('registration_step_complete_dob_screen');
        } catch (error) {
            console.log(error);
        } finally {
            setIsLoading(false);
        }
    };

    const goalDobFieldChange = useCallback(
        throttle(() => {
            callYaMetrika('entered_in_DOB');
            eventsApi.updateUserEvent('registration_dob');
        }, 2000),
        [],
    );

    const dob = watch('dob');

    useEffect(() => {
        eventsApi.updateUserEvent('registration_step_view_dob_screen');
    }, [])

    const handleChangeGender = (value: any) => {
        methods.setValue('gender', value);
        handleChangeUserDataDebounce({ gender: value });
    };

    const DELAY_SEND_DATA = 1000;

    const handleChangeUserDataDebounce = useCallback(
        debounce(async (data: any) => {
            await onUpdateUserData(data);
        }, DELAY_SEND_DATA),
        [onUpdateUserData],
    );

    const { t } = useTranslation();

    return (
        <FormProvider {...methods}>
            <form
                className={styles.form}
                onSubmit={handleSubmit(onSubmit)}>
                <div className={styles.formContainer}>
                    <Typography
                        variant="p"
                        fontWeight="medium"
                        className={styles.titleForm}
                        center>
                        {t('dob_title')}
                    </Typography>
                    <Typography
                        variant="p"
                        fontWeight="medium"
                        className={styles.subtitleForm}
                        center>
                        <Trans i18nKey="registration.dob_subtitle" />
                    </Typography>

                    <Controller
                        control={methods.control}
                        name={'dob'}
                        render={({ field: { onChange, value, ref } }) => {
                            return (
                                <DataPicker
                                    className={styles.datePicker}
                                    dob={methods.getValues('dob')}
                                    isRequireChanges={true}
                                    onChangeDate={(e) => {
                                        onChange(e)
                                        goalDobFieldChange();
                                    }} />
                            )}} />
                    
                    <div className={styles.genderForm}>
                        <div className={styles.genderWrapper}>
                            <div className={styles.genderChoice}>
                                <RadioCupertino
                                    label={t('i_female')}
                                    value="female"
                                    {...register('gender', {
                                        onChange(e) {
                                            handleChangeGender(e.target.value);
                                        },
                                    })} />
                            </div>

                            <div className={styles.genderChoice}>
                                <RadioCupertino
                                    label={t('i_male')}
                                    value="male"
                                    {...register('gender', {
                                        onChange(e) {
                                            handleChangeGender(e.target.value);
                                        },
                                    })} />
                            </div>
                        </div>
                    </div>
                </div>
                <SignUpActions>
                    <ButtonSubmitStep
                        isLoading={isLoading}
                        stepNumber={2}
                        isDisabled={!isValid}
                        onSkip={nextStep} />
                </SignUpActions>
            </form>
        </FormProvider>
    );
};
